import React, { useEffect } from 'react';
import { AiOutlineWarning } from 'react-icons/ai';
import { BiError } from 'react-icons/bi';
import { BsCheckCircle } from 'react-icons/bs';
import { useLocation, useNavigate } from 'react-router-dom';

import { ScrollToSection } from '@/components/common/ScrollToSection ';
import CreateWrapper from '@/features/scenario/create/CreateWrapper';

import { useAppDispatch, useAppSelector } from '../app/hook';
import { useGetProfileQuery } from '../app/services/auth-rtk.service';
import { useGetBotsQuery, useGetPackageQuery } from '../app/services/chatbot-rtk.services';
import DashboardHeaderComponent from '../components/dashboard/dashboard-header.component';
import DashboardMenuComponent from '../components/dashboard/dashboard-menu.component';
import LivePreview from '../components/dashboard/knowledge-bases-create/scenario/text-scenario/LivePreview';
import WelcomeTrialModal from '../components/dashboard/WelcomeTrialModal';
import ApiFailure from '../components/ui/ApiFailure';
import ApiLoading from '../components/ui/ApiLoading';
import UpgradeModal from '../components/ui/UpgradeModal';
import { handleResetAppearance, selectAppearanceState } from '../features/appearance/appearanceSlice';
import { selectIsImpersonate, selectIsUserNew } from '../features/auth/auth.slice';
import BotIntegrationModal from '../features/bot/BotIntegrationModal';
import {
  botsUiState,
  handleResetWizard,
  handleSetLivePreviewTargetBot,
  handleToggleBotsIntegrationModal,
  handleToggleCloneShareModal,
  wizardState,
} from '../features/bot/botSlice';
import CloneShareModal from '../features/bot/CloneShareModal';
import { selectChosenPlan, setIsFloatSideMenuShow } from '../features/dashboard.slice';
import { setBotVersions } from '../features/knowledge.slice';
import useScrollToTop from '../hooks/useScrollTop';
import useVersion from '../hooks/useVersion';

export const ToastIcons = (props: any) => {
  if (props.type == 'success') {
    return <BsCheckCircle color="#37A36E" width="24px" height="24px" fontSize="24px" />;
  } else if (props.type == 'warning') {
    return <AiOutlineWarning color="#f1c40f" width="24px" height="24px" fontSize="24px" />;
  } else {
    return <BiError color="#ff2d55" width="24px" height="24px" fontSize="24px" />;
  }
};

const DashboardLayout = ({ children }: { children: React.ReactNode }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const isImpersonate = useAppSelector(selectIsImpersonate);

  useVersion();
  useScrollToTop();

  const isUserNew = useAppSelector(selectIsUserNew);
  const chosePlan = useAppSelector(selectChosenPlan);
  const wizard = useAppSelector(wizardState);
  const appearanceState = useAppSelector(selectAppearanceState);
  const { showCloneShareModal, showIntegrationModal, livePreviewTargetBot } = useAppSelector(botsUiState);

  // Bootstrap initial data
  const getClient = useGetPackageQuery();
  const getBotVersion = useGetBotsQuery(undefined, {
    skip: isUserNew,
  });
  useGetProfileQuery(undefined, {
    skip: isUserNew,
  });

  // TODO ==> Remove following and manage via state
  useEffect(() => {
    if (getBotVersion.isSuccess && getBotVersion.data) {
      dispatch(setBotVersions(getBotVersion.data));
    }
  }, [getBotVersion.isSuccess]);

  // TODO ===> Remove when refactoring billing process after sign-up
  useEffect(() => {
    if (isUserNew && getClient.isSuccess && chosePlan && !location.pathname.includes('billing-checkout') && !location.pathname.includes('profile')) {
      navigate('billing-create');
    } else if (isUserNew && getClient.isSuccess && !location.pathname.includes('billing-checkout') && !location.pathname.includes('profile')) {
      if (location.search) {
        navigate('billing-create' + location.search);
      } else {
        // path name user free can be accessed
        if (location.pathname.includes('contact')) {
          return;
        }

        navigate('billing-create');
      }
    }

    if (location.pathname.includes('billing-checkin') && isUserNew) {
      dispatch(setIsFloatSideMenuShow(true));
    } else {
      dispatch(setIsFloatSideMenuShow(false));
    }

    if (!location.pathname.includes('knowledge-bases-create')) {
      localStorage.removeItem('persist:knowledge');
    }
  }, [isUserNew, location.pathname, getClient.isSuccess]);

  useEffect(() => {
    const handlePopState = () => {
      if (wizard.showModal) {
        dispatch(handleResetWizard());
        //navigate(1);
      }

      if (appearanceState.showModal) {
        dispatch(handleResetAppearance());
        // navigate(1);
      }

      if (showCloneShareModal) dispatch(handleToggleCloneShareModal({ status: false }));

      if (showIntegrationModal) dispatch(handleToggleBotsIntegrationModal({ status: false }));

      if (livePreviewTargetBot) dispatch(handleSetLivePreviewTargetBot(''));
    };

    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [wizard.showModal, appearanceState.showModal, showCloneShareModal, showIntegrationModal, livePreviewTargetBot]);

  useEffect(() => {
    if (livePreviewTargetBot || showCloneShareModal || appearanceState.showModal || wizard.showModal || showIntegrationModal)
      dispatch(handleSetLivePreviewTargetBot(''));
  }, [location, wizard.showModal, appearanceState.showModal, showCloneShareModal, showIntegrationModal]);

  useEffect(() => {
    window.addEventListener('message', (event: MessageEvent<{ action: string; [key: string]: any }>) => {
      if (event.data.action === 'resizeIframe' && event.data.width === 0 && event.data.height === 0) {
        dispatch(handleSetLivePreviewTargetBot(''));
      }
    });

    return () => {
      window.removeEventListener('message', () => {
        dispatch(handleSetLivePreviewTargetBot(''));
      });
    };
  }, []);

  let content;

  if (getClient.isLoading || getBotVersion.isLoading)
    content = (
      <div className="tw-h-screen tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center">
        {' '}
        <ApiLoading />
      </div>
    );

  if (getClient.isError)
    content = (
      <div className="tw-h-screen tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center">
        {' '}
        <ApiFailure refetch={() => getClient.refetch()} />
      </div>
    );

  if (getBotVersion.isError)
    content = (
      <div className="tw-h-screen tw-w-full tw-flex tw-flex-col tw-justify-center tw-items-center">
        {' '}
        <ApiFailure refetch={() => getBotVersion.refetch()} />
      </div>
    );

  if (getClient.isSuccess) {
    content = (
      <React.Fragment>
        <ScrollToSection />
        {/* Dashboard side menu */}
        <DashboardMenuComponent />
        <main className="tw-bg-gray-100 tw-h-svh tw-max-h-svh tw-overflow-auto tw-w-full">
          {/* fixed header for layout including mobile menu trigger */}
          <DashboardHeaderComponent />

          {/* Main layout content (pages will be rendered inside main) */}
          <div className="content tw-overflow-y-auto tw-w-full tw-h-[calc(100svh-64px)]">{children}</div>
        </main>
        {/* Wizard modal to be accessible all over the dashboard */}
        {/* <BotWizard /> */}
        <CreateWrapper />
        {/* Bot integration modal */}
        <BotIntegrationModal />
        {/* Clone share modal*/}
        <CloneShareModal />
        {/* Live preview iframe integration */}
        <LivePreview />
        {/* Fist visit try 14 days modal */}
        <WelcomeTrialModal />
        {/* Upgrade modal (With promo) */}
        <UpgradeModal />
      </React.Fragment>
    );
  }

  return (
    <div className={isImpersonate ? 'tw-border-solid tw-border-red-600 tw-border-[15px] tw-w-full tw-h-full tw-flex' : 'tw-w-full tw-h-full tw-flex'}>
      {content}
    </div>
  );
};

export default DashboardLayout;
